import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Skeleton } from "../ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  MATIERES,
  NIVEAUX,
  RESOURCE_TYPES,
  NiveauBadge,
  MatiereBadge,
  ResourceTypeBadge,
  formatRelativeTime,
} from "./constants";
import { getSharedResources, duplicateResource } from "../../lib/api";
import {
  Search,
  Download,
  ExternalLink,
  FileText,
  Users,
  SlidersHorizontal,
  X,
} from "lucide-react";

function SharedResourceCard({ resource, onImport }) {
  return (
    <Card className="group hover:shadow-md transition-all duration-200 overflow-hidden">
      <CardContent className="p-0">
        <div className="h-28 bg-gradient-to-br from-slate-50 to-slate-100 relative">
          {resource.thumbnail ? (
            <img
              src={resource.thumbnail}
              alt={resource.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <FileText className="w-10 h-10 text-slate-300" />
            </div>
          )}
          <div className="absolute top-2 left-2">
            <Badge variant="secondary" className="text-xs bg-white/90">
              <Users className="w-3 h-3 mr-1" />
              Partage
            </Badge>
          </div>
        </div>

        <div className="p-3">
          <h3 className="font-semibold text-slate-800 text-sm line-clamp-1 mb-2">
            {resource.title}
          </h3>

          <div className="flex flex-wrap gap-1 mb-3">
            {resource.niveau && <NiveauBadge niveau={resource.niveau} className="text-xs" />}
            {resource.matiere && <MatiereBadge matiere={resource.matiere} className="text-xs" />}
            {resource.resource_type && <ResourceTypeBadge type={resource.resource_type} className="text-xs" />}
          </div>

          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400">
              {formatRelativeTime(resource.updated_at)}
            </span>
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={() => onImport(resource)}
            >
              <Download className="w-3 h-3 mr-1" />
              Importer
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function TeacherSharedResourcesTab() {
  const navigate = useNavigate();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    niveau: "",
    matiere: "",
    resource_type: "",
  });
  const [showFilters, setShowFilters] = useState(false);

  const fetchResources = async () => {
    setLoading(true);
    try {
      const params = { limit: 50 };
      if (search) params.search = search;
      if (filters.niveau) params.niveau = filters.niveau;
      if (filters.matiere) params.matiere = filters.matiere;
      if (filters.resource_type) params.resource_type = filters.resource_type;

      const { data } = await getSharedResources(params);
      setResources(data);
    } catch (err) {
      console.error("Error fetching shared resources:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const debounce = setTimeout(fetchResources, 300);
    return () => clearTimeout(debounce);
  }, [search, filters]);

  const handleImport = async (resource) => {
    try {
      const { data } = await duplicateResource(resource.id);
      // Navigate to the imported resource
      if (data.elements) {
        navigate(`/create?id=${data.id}`);
      } else {
        navigate(`/editor/${data.id}`);
      }
    } catch (err) {
      console.error("Error importing resource:", err);
    }
  };

  const clearFilters = () => {
    setFilters({ niveau: "", matiere: "", resource_type: "" });
    setSearch("");
  };

  const hasActiveFilters = Object.values(filters).some(Boolean) || search;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-bold text-slate-800 mb-1">Ressources partagees</h2>
        <p className="text-slate-500 text-sm">
          Decouvrez et importez les ressources partagees par la communaute
        </p>
      </div>

      {/* Search and Filters */}
      <div className="space-y-3">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Rechercher dans les ressources partagees..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button
            variant={showFilters ? "secondary" : "outline"}
            onClick={() => setShowFilters(!showFilters)}
          >
            <SlidersHorizontal className="w-4 h-4 mr-2" />
            Filtres
            {hasActiveFilters && (
              <Badge className="ml-2 bg-indigo-600">!</Badge>
            )}
          </Button>
        </div>

        {showFilters && (
          <div className="p-4 bg-slate-50 rounded-lg space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Select
                value={filters.niveau || "all"}
                onValueChange={(v) => setFilters({ ...filters, niveau: v === "all" ? "" : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Niveau" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les niveaux</SelectItem>
                  {NIVEAUX.map((n) => (
                    <SelectItem key={n.value} value={n.value}>{n.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={filters.matiere || "all"}
                onValueChange={(v) => setFilters({ ...filters, matiere: v === "all" ? "" : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Matiere" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les matieres</SelectItem>
                  {MATIERES.map((m) => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={filters.resource_type || "all"}
                onValueChange={(v) => setFilters({ ...filters, resource_type: v === "all" ? "" : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les types</SelectItem>
                  {RESOURCE_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="w-4 h-4 mr-1" />
                Effacer les filtres
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Resources Grid */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      ) : resources.length === 0 ? (
        <Card className="bg-slate-50 border-dashed">
          <CardContent className="py-12 text-center">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 mb-2">
              {hasActiveFilters
                ? "Aucune ressource partagee ne correspond aux filtres"
                : "Aucune ressource partagee pour le moment"
              }
            </p>
            <p className="text-sm text-slate-400 mb-4">
              Partagez vos propres ressources depuis "Mes ressources" pour contribuer !
            </p>
            {hasActiveFilters && (
              <Button variant="outline" onClick={clearFilters}>
                Effacer les filtres
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {resources.map((resource) => (
            <SharedResourceCard
              key={resource.id}
              resource={resource}
              onImport={handleImport}
            />
          ))}
        </div>
      )}
    </div>
  );
}
